/* change later */
import { OperatorEnum } from "@src/pages/intelligence-search/enum";
import { SmlAsset, SmlJsonQuery, SmlJsonQueryFilter } from "@src/types/intelligence-search";
import { uniqBy } from "lodash";
import React, { createContext, useContext } from "react";

/**
 * Inserts a primitive item into an array if it's not already present.
 * Returns a new array (immutable operation).
 * @param {Array<T>} arr - The original array.
 * @param {T} item - The item to insert.
 * @returns {Array<T>} A new array with the item inserted if missing.
 */
function insertIfMissingPrimitive<T>(arr: T[], item: T): T[] {
    if (arr.includes(item)) {
        return arr; // Item already exists, return original array (no change needed)
    }
    return [...arr, item]; // Item missing, return new array with item added
}

function moveItemInArray<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
    if (fromIndex < 0 || fromIndex >= arr.length || toIndex < 0 || toIndex >= arr.length) {
        return arr; // Return original array if indices are invalid
    }

    const element = arr.splice(fromIndex, 1)[0]; // remove element from original position
    arr.splice(toIndex, 0, element); // insert element at the new position
    return arr;
}

export type QueryDef = {
    index: number;
    queryName: string;
    jsonQuery?: SmlJsonQuery;
};

/*
Example: {
    queryKey1: {
        queryKey2: [assetName]
    }
}

*/
export type DependenciesGraph = Record<string, Record<string, string[]>>;

type QueryDependenciesGraphContextType = {
    upsertQuery: (
        key: string,
        index: number,
        queryName: string,
        q: SmlJsonQuery,
        dependencies?: Record<string, string[]>,
    ) => void;
    removeQuery: (key: string) => void;
    getQueryDef: (key: string) => QueryDef;
    getUpstreams: (key: string) => Record<string, string[]>;
    upsertDependencies: (key: string, dependencies: Record<string, string[]>) => void;
    upsertPredecessor: (
        queryKey1: string,
        queryKey2: string,
        assetName: string,
        operator: OperatorEnum,
    ) => SmlJsonQueryFilter;
    removePredecessor: (queryKey1: string, queryKey2: string, assetName: string) => void;
    isPredecessor: (key: string, assetName?: string) => boolean;
    getGeneratedFilters: (key: string, operator: OperatorEnum) => SmlJsonQueryFilter[] | undefined;
    getDependableQueries: (key: string) => Record<string, QueryDef>;
    generateJsonQuery: (key: string, operator: OperatorEnum, extraJsonQuery?: SmlJsonQuery) => SmlJsonQuery | undefined;
    meta?: SmlAsset[];
    productCode?: string;
    queries?: Record<string, QueryDef>;
    dependenciesGraph?: DependenciesGraph;
    metaDictionary: Record<string, SmlAsset | undefined>;
    isFirstQuestion: (key: string) => boolean;
    queryAssetMeta: Record<string, Record<string, SmlAsset | undefined>>;
    setQueryAssetMeta: React.Dispatch<React.SetStateAction<Record<string, Record<string, SmlAsset | undefined>>>>;
};

type QueryDependenciesGraphProviderProps = React.PropsWithChildren & {
    productCode?: string;
    metaDictionary: Record<string, SmlAsset | undefined>;
};

export const QueryDependenciesGraphContext = createContext({} as QueryDependenciesGraphContextType);

export const QueryDependenciesGraphProvider: React.FC<QueryDependenciesGraphProviderProps> = ({
    metaDictionary,
    children,
    productCode,
}) => {
    const [queries, setQueries] = React.useState<Record<string, QueryDef>>({});
    const [queryAssetMeta, setQueryAssetMeta] = React.useState<Record<string, Record<string, SmlAsset | undefined>>>(
        {},
    );
    const [dependenciesGraph, setDependenciesGraph] = React.useState<DependenciesGraph>({});

    const upsertQuery = (
        key: string,
        index: number,
        queryName: string,
        q: SmlJsonQuery,
        dependencies?: Record<string, string[]>,
    ) => {
        setQueries((prev) => {
            const { [key]: _, ...rest } = prev;
            return {
                ...rest,
                [key]: {
                    index,
                    queryName,
                    jsonQuery: q,
                },
            };
        });

        if (dependencies) {
            setDependenciesGraph((prev) => {
                const { [key]: _, ...rest } = prev;
                return {
                    ...rest,
                    [key]: dependencies,
                };
            });
        }
    };

    const removeQuery = (key: string): void => {
        setQueries((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
        });
        setDependenciesGraph((prev) => {
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    };

    const getQueryDef = (key: string): QueryDef => {
        return queries[key] || {};
    };

    const generateFilter = (predecessorKey: string, assetName: string, operator?: OperatorEnum): SmlJsonQueryFilter => {
        return {
            name: assetName,
            operator: operator,
            values: [generateJsonQuery(predecessorKey, operator) || {}],
            meta: metaDictionary[assetName],
        };
    };

    const getUpstreams = (key: string) => {
        return dependenciesGraph[key] ?? {};
    };

    const upsertDependencies = (key: string, dependencies: Record<string, string[]>) => {
        setDependenciesGraph((prev) => {
            const { [key]: _, ...rest } = prev;
            return {
                ...rest,
                [key]: dependencies,
            };
        });
    };

    const upsertPredecessor = (
        queryKey1: string,
        queryKey2: string,
        assetName: string,
        operator: OperatorEnum,
    ): SmlJsonQueryFilter => {
        // todo: validate
        setDependenciesGraph((prev) => {
            const { [queryKey1]: upstreams, ...rest } = prev;
            const assetNames = upstreams[queryKey2] ?? [];

            return {
                ...rest,
                [queryKey1]: {
                    [queryKey2]: insertIfMissingPrimitive(assetNames, assetName),
                },
            };
        });

        return generateFilter(queryKey2, assetName, operator);
    };

    const removePredecessor = (queryKey1: string, queryKey2: string, assetName: string) => {
        // todo: validate
        setDependenciesGraph((prev) => {
            const { [queryKey1]: upstreams, ...rest } = prev;
            const assetNames = upstreams[queryKey2] ?? [];

            return {
                ...rest,
                [queryKey1]: {
                    [queryKey2]: assetNames.filter((n) => n !== assetName) || [],
                },
            };
        });
    };

    const isPredecessor = (key: string, assetName?: string) => {
        const { [key]: deps, ...rest } = dependenciesGraph;

        if (rest === undefined) return false;
        if (assetName) {
            return Object.entries(rest).some(([_, deps]) => {
                return deps[key]?.some((d) => d.includes(assetName));
            });
        }
        return (
            Object.entries(rest)
                .flatMap(([_, deps]) => {
                    return Object.keys(deps || {});
                })
                .find((d) => d === key) !== undefined
        );
    };

    const getGeneratedFilters = (key: string, operator?: OperatorEnum): SmlJsonQueryFilter[] => {
        // todo: validate

        return (
            Object.entries(dependenciesGraph[key] || {}).flatMap((v) => {
                return v[1].map((assetName) => generateFilter(v[0], assetName, operator));
            }) || []
        );
    };

    const hasCycle = (node: string, visited = new Set()) => {
        if (visited.has(node)) return true;

        visited.add(node);

        for (const dep of Object.keys(dependenciesGraph[node] || {})) {
            if (hasCycle(dep, visited)) return true;
        }

        return false;
    };

    const isCreateCircularGraph = (predecessorKey: string, key: string): boolean => {
        if (dependenciesGraph[predecessorKey] === undefined) return false;

        // todo: whether create connection from predecessorKey to key make dependenciesGraph become circular graph
        return hasCycle(predecessorKey, new Set([key]));
    };

    const getDependableQueries = (key: string): Record<string, QueryDef> => {
        const { [key]: _, ...rest } = queries;
        return (
            Object.entries(rest || {})
                .map((v) => {
                    return { [v[0]]: v[1] };
                })
                .reduce((v1, v2) => Object.assign(v1, v2), {}) || {}
        );
    };

    const generateJsonQuery = (
        key: string,
        operator?: OperatorEnum,
        extraJsonQuery?: SmlJsonQuery,
    ): SmlJsonQuery | undefined => {
        if (!queries[key]) return;
        const currQuery = queries[key].jsonQuery;
        const deps = dependenciesGraph[key];
        const _currentSelect = uniqBy([...(extraJsonQuery?.select || []), ...(currQuery?.select || [])], (item) => {
            if (typeof item === "string") {
                return item;
            }
            return item.alias;
        });
        const _currentFilters = ([] as Pick<SmlJsonQueryFilter, "name" | "operator" | "values">[])
            .concat(extraJsonQuery?.filters || [])
            .concat(currQuery?.filters || [])
            .filter((f, index, self) => {
                return (
                    index ===
                    self.findIndex((t) => {
                        return t.name === f.name;
                    })
                );
            })
            .map((f) => {
                return {
                    name: f.name,
                    operator: f.operator,
                    values: f.values,
                };
            });
        // return raw json query if there is no dependency
        if (!deps || Object.keys(deps).length === 0)
            return { ...(currQuery ?? {}), select: _currentSelect, filters: _currentFilters };

        // attach dependencies to query

        return {
            select: _currentSelect,
            filters: [..._currentFilters, ...(getGeneratedFilters(key, operator) || [])],
            orders: currQuery?.orders,
            limit: currQuery?.limit,
        };
    };
    const isFirstQuestion = (key: string) => {
        return Object.keys(queries).length === 1 || (queries[key] && queries[key].index === 0);
    };
    return (
        <QueryDependenciesGraphContext.Provider
            value={{
                setQueryAssetMeta,
                queryAssetMeta,
                upsertQuery,
                removeQuery,
                getQueryDef,
                getUpstreams,
                upsertDependencies,
                upsertPredecessor,
                removePredecessor,
                isPredecessor,
                getGeneratedFilters,
                getDependableQueries,
                generateJsonQuery,
                isFirstQuestion,
                productCode,
                queries,
                dependenciesGraph,
                metaDictionary,
            }}
        >
            {children}
        </QueryDependenciesGraphContext.Provider>
    );
};
export const useQueryDependenciesGraphContext = () => useContext(QueryDependenciesGraphContext);
